import type { DocumentStatusState, RealtimeDocStatus } from "../hooks/useDocumentProcessingStream";
import { resolveExtractionProgress } from "./documentExtractionJobs";

export type ExtractionCardStatus =
  | "idle"
  | "queued"
  | "processing"
  | "in_review"
  | "complete"
  | "failed"
  | "stuck";

/** Docs at 0% queued longer than this show retry instead of progress overlay. */
export const STUCK_QUEUE_THRESHOLD_MS = 15 * 60 * 1000;

/** True when document is actively in the extraction pipeline (queued or processing). */
export function isActiveExtractionStatus(status?: string | null): boolean {
  const n = normalizeEtlStatus(status);
  return n === "pending" || n === "processing";
}

export function normalizeEtlStatus(raw?: string | null): RealtimeDocStatus | null {
  if (!raw) return null;
  const s = raw.toLowerCase();
  if (s === "skipped") return null;
  if (s === "queued") return "pending";
  if (s === "fail") return "failed";
  // Legacy DB/SSE value: extraction finished, awaiting user review (not user-submitted).
  if (s === "completed") return "in_review";
  if (s === "pending_user_review") return "in_review";
  if (
    s === "pending" ||
    s === "processing" ||
    s === "in_review" ||
    s === "complete" ||
    s === "failed"
  ) {
    return s as RealtimeDocStatus;
  }
  return null;
}

/** Extraction pipeline finished (success or failure) — stops progress tracking. */
export function isTerminalEtlStatus(status?: string | null): boolean {
  const n = normalizeEtlStatus(status);
  return n === "in_review" || n === "complete" || n === "failed";
}

/** User confirmed extracted data — fully completed workflow. */
export function isEtlComplete(status?: string | null): boolean {
  return normalizeEtlStatus(status) === "complete";
}

/** AI extraction finished and awaits user review. */
export function isEtlInReview(status?: string | null): boolean {
  return normalizeEtlStatus(status) === "in_review";
}

/** Stream may send 100% while status is still "processing" — treat as done for card UI. */
export function isExtractionUiComplete(
  status?: string | null,
  progress?: number | null
): boolean {
  if (isEtlComplete(status) || isEtlInReview(status)) return true;
  const n = normalizeEtlStatus(status);
  if (n === "failed") return true;
  return (
    progress != null &&
    progress >= 100 &&
    (n === "processing" || n === "pending" || n === null)
  );
}

/**
 * When API and local job state disagree, prefer API for workflow accuracy
 * (e.g. API in_review must not be shown as complete from a stale job).
 */
export function mergeEtlStatuses(
  apiStatus?: string | null,
  jobStatus?: string | null
): string | null {
  const apiN = normalizeEtlStatus(apiStatus);
  const jobN = normalizeEtlStatus(jobStatus);
  if (apiN === "in_review" && isEtlComplete(jobN)) {
    return apiStatus ?? "in_review";
  }
  if (isEtlComplete(apiN) && jobN === "in_review") {
    return apiStatus ?? "complete";
  }
  // Stale job complete before API/search catches up — never show complete from job alone.
  if (isEtlComplete(jobN) && apiN && apiN !== "complete") {
    return apiStatus ?? null;
  }
  if (isEtlComplete(jobN) && !apiN) {
    return "in_review";
  }
  if (apiN && !jobN) return apiStatus ?? null;
  if (jobN && !apiN) return jobStatus ?? null;
  return jobStatus ?? apiStatus ?? null;
}

/** Map normalized ETL status to UI/badge string (queued, in_review, complete, …). */
export function resolveEtlDisplayStatus(
  status?: string | null,
  progress?: number | null
): string {
  const n = normalizeEtlStatus(status);
  if (n === "failed") return "failed";
  if (isEtlComplete(n)) return "complete";
  if (isEtlInReview(n)) return "in_review";
  if (isExtractionUiComplete(n, progress)) return "in_review";
  if (n === "pending") return "queued";
  if (n === "processing") return "processing";
  return status?.trim() || "idle";
}

export function isExtractionStuckInQueue(
  status: string | null | undefined,
  progress: number | null | undefined,
  queuedSince: string | null | undefined
): boolean {
  if (normalizeEtlStatus(status) !== "pending") return false;
  if (progress != null && progress > 0) return false;
  if (!queuedSince) return false;
  const queuedAt = new Date(queuedSince).getTime();
  if (Number.isNaN(queuedAt)) return false;
  return Date.now() - queuedAt >= STUCK_QUEUE_THRESHOLD_MS;
}

export function getExtractionCardState(
  docId: string,
  docEtl?: { status?: string | null; progress?: number | null; stage?: string | null; error?: string | null } | null,
  statusMap?: Record<string, DocumentStatusState>,
  isWatched?: boolean,
  queuedSince?: string | null
): {
  cardStatus: ExtractionCardStatus;
  progress: number | null;
  stage: string | null;
  error: string | null;
  clickable: boolean;
} {
  const live = statusMap?.[docId];
  const rawStatus =
    mergeEtlStatuses(docEtl?.status, live?.status) ??
    live?.status ??
    docEtl?.status ??
    null;
  const normalized = normalizeEtlStatus(rawStatus);

  if (normalized === "failed") {
    return {
      cardStatus: "failed",
      progress: null,
      stage: live?.stage ?? docEtl?.stage ?? null,
      error: live?.error ?? docEtl?.error ?? null,
      clickable: true,
    };
  }

  if (isEtlInReview(normalized)) {
    return {
      cardStatus: "in_review",
      progress: 100,
      stage: live?.stage ?? docEtl?.stage ?? null,
      error: null,
      clickable: true,
    };
  }

  if (isEtlComplete(normalized)) {
    return {
      cardStatus: "complete",
      progress: 100,
      stage: live?.stage ?? docEtl?.stage ?? null,
      error: null,
      clickable: true,
    };
  }

  const progress = resolveExtractionProgress(live?.progress, docEtl?.progress);
  const resolvedProgress =
    progress != null
      ? progress
      : normalizeEtlStatus(rawStatus) === "pending"
        ? 0
        : null;

  if (
    (isWatched || live) &&
    isExtractionStuckInQueue(
      rawStatus,
      resolvedProgress,
      queuedSince ?? null
    )
  ) {
    return {
      cardStatus: "stuck",
      progress: null,
      stage: live?.stage ?? docEtl?.stage ?? null,
      error: null,
      clickable: true,
    };
  }

  if (isExtractionUiComplete(normalized ?? docEtl?.status, resolvedProgress)) {
    return {
      cardStatus: isEtlComplete(normalized) ? "complete" : "in_review",
      progress: 100,
      stage: live?.stage ?? docEtl?.stage ?? null,
      error: null,
      clickable: true,
    };
  }

  const isActivelyTracked = Boolean(isWatched) || Boolean(live);
  if (
    isActivelyTracked &&
    (normalized === "pending" || normalized === "processing")
  ) {
    return {
      cardStatus: normalized === "pending" ? "queued" : "processing",
      progress: resolvedProgress,
      stage: live?.stage ?? docEtl?.stage ?? null,
      error: null,
      clickable: false,
    };
  }

  return {
    cardStatus: "idle",
    progress: null,
    stage: null,
    error: null,
    clickable: true,
  };
}
